import { useCallback } from 'react';
import { useSearch, ISearchResults } from 'src/utilities/useSearchHooks';
import {
  TableAdapter,
  TableState,
  TableAdapterStatus,
  SearchFilters,
} from 'src/components/DataTable/types/DataTable';
import {
  mapTableStateToSearchParams,
  mapSearchStatusToTableAdapterStatus,
} from './useSearchAdapter.utils';

export interface SearchAdapterConfig {
  searchUrl: string;
}

export function createSearchAdapter<TData, TFilters = SearchFilters>(
  config: SearchAdapterConfig
) {
  return function useSearchAdapterHook(
    initialState: TableState<TFilters>
  ): TableAdapter<TData, TFilters> {
    const { searchUrl } = config;

    const defaultSearchParams = mapTableStateToSearchParams(initialState);
    const searchResults: ISearchResults<TData> = useSearch<TData>(
      searchUrl,
      defaultSearchParams
    );

    const { searchStatus, resultsList, totalResults, error, doSearch } =
      searchResults;

    const status: TableAdapterStatus =
      mapSearchStatusToTableAdapterStatus(searchStatus);

    const fetch = useCallback(
      async (state: TableState<TFilters>): Promise<void> => {
        const searchParams = mapTableStateToSearchParams(state);
        await doSearch(searchParams);
      },
      // doSearch is intentionally not in deps:
      // because it’s recreated whenever searchResults changes, so including it here
      // would cause fetch to change on every render and trigger an infinite
      // search -> setSearchResults -> search loop.
      // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
      [searchUrl]
    );

    const adapter = {
      data: resultsList,
      total: totalResults,
      status,
      ...(status === TableAdapterStatus.ERROR && { error }),
      fetch,
    };

    return adapter;
  };
}
